import { useCallback, useEffect, useState } from 'react';
import { tokens } from '../../components/forms/tokens';
import { formatDateTime } from '../../lib/datetime';
import { cleanMfaInput, isMfaCode, stepUpErrorMessage } from '../../lib/mfaApproval';
import { ROLE_LABEL } from '../../lib/roleRouting';
import adminService, { type StaffChange } from '../../services/admin.service';
import { extractError } from './_userActions';
import {
  DECISION_BLOCK_MESSAGE,
  KIND_LABEL,
  canCancel,
  changeSubjectEmail,
  changeSubjectName,
  decisionBlock,
} from './staffChanges.helpers';

/**
 * The changes that give or take away the manager or the super admin role and wait for a second
 * super admin (#326). A different super admin confirms one with their code and it takes effect
 * at once; any super admin but the person it names may cancel it with a note.
 */
export default function PendingStaffChanges({
  refreshKey,
  onDecided,
}: Readonly<{
  /** Changes when the page proposed something, so the list is read again. */
  refreshKey: number;
  onDecided: (message: string) => void;
}>): JSX.Element {
  const [changes, setChanges] = useState<StaffChange[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setChanges(await adminService.listStaffChanges());
    } catch (e) {
      setError(extractError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load, refreshKey]);

  const decided = (message: string): void => {
    onDecided(message);
    void load();
  };

  return (
    <section className="rounded-2xl p-5 mb-5" style={{ background: tokens.surface }}>
      <h2 className="text-base font-semibold mb-1" style={{ color: tokens.textPrimary }}>
        Changes waiting for a second super admin
      </h2>
      <p className="text-sm mb-3" style={{ color: tokens.textMuted }}>
        Creating, deactivating or reactivating a manager or a super admin, and changing a role to
        or from either, takes two super admins. The person a change is about never decides on it.
      </p>
      {loading && (
        <p className="text-sm" style={{ color: tokens.textMuted }}>Loading…</p>
      )}
      {!loading && error && (
        <div
          className="p-3 rounded-lg text-sm"
          role="alert"
          style={{ background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`, color: tokens.errorText }}
        >
          The changes waiting could not be loaded. {error}
        </div>
      )}
      {!loading && !error && changes.length === 0 && (
        <p className="text-sm" style={{ color: tokens.textMuted }}>No change is waiting.</p>
      )}
      {!loading && !error && changes.length > 0 && (
        <ul className="grid gap-3">
          {changes.map((change) => (
            <PendingChange key={change.id} change={change} onDecided={decided} />
          ))}
        </ul>
      )}
    </section>
  );
}

function PendingChange({
  change,
  onDecided,
}: Readonly<{ change: StaffChange; onDecided: (message: string) => void }>): JSX.Element {
  const block = decisionBlock(change);
  const [mode, setMode] = useState<'idle' | 'confirm' | 'cancel'>('idle');
  const [code, setCode] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const confirm = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await adminService.confirmStaffChange(change.id, code);
      onDecided('Change confirmed. It is in effect now.');
    } catch (e) {
      setError(stepUpErrorMessage(e, extractError(e)));
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await adminService.cancelStaffChange(change.id, note.trim());
      onDecided('Change cancelled.');
    } catch (e) {
      setError(extractError(e));
    } finally {
      setBusy(false);
    }
  };

  const fieldStyle = { border: `1px solid ${tokens.border}` };

  return (
    <li className="rounded-lg p-3" style={{ border: `1px solid ${tokens.border}` }}>
      <div className="flex flex-wrap justify-between gap-2">
        <div>
          <div className="text-sm font-semibold" style={{ color: tokens.textPrimary }}>
            {KIND_LABEL[change.kind]} · {changeSubjectName(change)}
          </div>
          <div className="text-xs font-mono" style={{ color: tokens.textMuted }}>
            {changeSubjectEmail(change)}
          </div>
          <div className="text-xs mt-1" style={{ color: tokens.textMuted }}>
            {change.kind === 'change_role' && change.previous_role ? (
              <>Role: {ROLE_LABEL[change.previous_role]} → {ROLE_LABEL[change.role]}</>
            ) : (
              <>Role: {ROLE_LABEL[change.role]}</>
            )}
          </div>
          <div className="text-xs mt-1" style={{ color: tokens.textMuted }}>
            Proposed by {change.proposed_by_name} · {formatDateTime(change.proposed_at)}
          </div>
          <div className="text-xs mt-1" style={{ color: tokens.textPrimary }}>
            Reason: {change.reason}
          </div>
        </div>
        {mode === 'idle' && (
          <div className="flex gap-2 items-start">
            {block === null && (
              <button
                type="button"
                onClick={() => setMode('confirm')}
                className="px-3 py-1.5 rounded-md text-xs font-semibold"
                style={{ background: tokens.infoText, color: '#FFFFFF' }}
              >
                Confirm
              </button>
            )}
            {canCancel(change) && (
              <button
                type="button"
                onClick={() => setMode('cancel')}
                className="px-3 py-1.5 rounded-md text-xs font-semibold"
                style={{ background: tokens.surfaceMuted, color: tokens.textPrimary }}
              >
                Cancel proposal
              </button>
            )}
          </div>
        )}
      </div>

      {mode === 'idle' && block !== null && (
        <p className="text-xs mt-2" style={{ color: tokens.textMuted }}>
          {DECISION_BLOCK_MESSAGE[block]}
        </p>
      )}

      {mode === 'confirm' && (
        <div className="mt-3 grid gap-2">
          <label className="block">
            <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>
              Confirm with the code from your authenticator app
            </span>
            <input
              value={code}
              onChange={(e) => setCode(cleanMfaInput(e.target.value))}
              inputMode="numeric"
              autoComplete="one-time-code"
              disabled={busy}
              className="w-40 px-3 py-2 rounded-lg text-sm"
              style={fieldStyle}
            />
          </label>
          <p className="text-xs" style={{ color: tokens.textMuted }}>
            The change takes effect as soon as you confirm it. A deactivation or a role change
            signs the person out everywhere.
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || !isMfaCode(code)}
              onClick={() => { void confirm(); }}
              className="px-3 py-1.5 rounded-md text-xs font-semibold"
              style={{ background: tokens.infoText, color: '#FFFFFF' }}
            >
              {busy ? 'Working…' : 'Confirm change'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setMode('idle')}
              className="px-3 py-1.5 rounded-md text-xs font-semibold"
              style={{ background: tokens.surfaceMuted, color: tokens.textPrimary }}
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {mode === 'cancel' && (
        <div className="mt-3 grid gap-2">
          <label className="block">
            <span className="block text-xs font-semibold mb-1" style={{ color: tokens.textLabel }}>
              Why it is cancelled (stored in the audit log)
            </span>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              maxLength={500}
              disabled={busy}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={fieldStyle}
            />
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={busy || note.trim().length < 4}
              onClick={() => { void cancel(); }}
              className="px-3 py-1.5 rounded-md text-xs font-semibold"
              style={{ background: tokens.errorText, color: '#FFFFFF' }}
            >
              {busy ? 'Working…' : 'Cancel proposal'}
            </button>
            <button
              type="button"
              disabled={busy}
              onClick={() => setMode('idle')}
              className="px-3 py-1.5 rounded-md text-xs font-semibold"
              style={{ background: tokens.surfaceMuted, color: tokens.textPrimary }}
            >
              Not now
            </button>
          </div>
        </div>
      )}

      {error && (
        <div
          className="mt-2 p-2 rounded-lg text-xs"
          role="alert"
          style={{ background: tokens.errorBg, border: `1px solid ${tokens.errorBorder}`, color: tokens.errorText }}
        >
          {error}
        </div>
      )}
    </li>
  );
}
